import {
  TextInput,
  TextInputProps,
  useMantineColorScheme,
} from "@mantine/core";
import { formatNumberAsString } from "common/utils";
import { useEffect, useRef, useState } from "react";

interface CustomNumberInputProps extends Omit<
  TextInputProps,
  "value" | "onChange" | "type"
> {
  readonlyMode: boolean;
  value: number;
  asCurrency?: boolean;
  wholeNumber?: boolean;
  maxLength?: number;
  onChange?: (value: number) => void;
}

export const CustomNumberInput: React.FC<CustomNumberInputProps> = ({
  readonlyMode,
  value,
  asCurrency = true,
  wholeNumber = false,
  maxLength = 9,
  onChange,
  styles,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const tmp = String(value ?? 0);
  const [localValue, setLocalValue] = useState(tmp);

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const labelColor = isDark ? "#e0e0e0" : "#1a1a1a";

  useEffect(() => {
    let formatted = String(value);
    if (wholeNumber) {
      formatted = formatNumberAsString(value ?? 0, 0, 0);
    } else if (asCurrency) {
      formatted = formatNumberAsString(value ?? 0);
    }
    setLocalValue(formatted);
  }, [value]);

  const handleChange = (raw: string) => {
    // Whole numbers only → digits only
    if (wholeNumber) {
      if (!/^\d*$/.test(raw)) return;
    } else {
      // Allow digits + optional decimal
      if (!/^\d*\.?\d*$/.test(raw)) return;
    }

    if (raw.length > maxLength) return;

    setLocalValue(raw);
  };

  const handleBlur = () => {
    const parsed = Number(localValue);
    const finalValue = isNaN(parsed) ? 0 : parsed;

    // Commit to parent
    onChange?.(finalValue);

    // Re-format only if currency
    if (asCurrency) {
      setLocalValue(formatNumberAsString(finalValue));
    }
  };

  return (
    <>
      <TextInput
        type="text"
        inputMode="decimal"
        readOnly={readonlyMode}
        ref={inputRef}
        value={localValue}
        maxLength={maxLength}
        onChange={(e) => handleChange(e.currentTarget.value)}
        onBlur={handleBlur}
        labelProps={{
          fw: 600,
          size: "lg",
          style: { color: labelColor },
        }}
        styles={{
          input: {
            backgroundColor: readonlyMode
              ? isDark
                ? "#2a2a2a"
                : "#f5f5f5"
              : isDark
                ? "#3a3a3a"
                : "white",

            borderColor: readonlyMode
              ? isDark
                ? "#555"
                : "#d0d0d0"
              : isDark
                ? "#4dabf7"
                : "#228be6",

            cursor: readonlyMode ? "default" : "text",
            //transition: "background-color 120ms ease, border-color 120ms ease",
          },
          ...styles,
        }}
        {...rest}
      />
    </>
  );
};
